import type { AgentMode, TaskContext } from "@project/shared";

enum PmRelaunchIntent {
  Build = "build",
  Review = "review",
  AutoWithPlan = "auto_with_plan",
  AutoPlanning = "auto_planning",
  WaitForTeam = "wait_for_team",
}

function resolvePmRelaunchIntent(
  context: TaskContext,
  isAuto?: boolean,
  agentMode?: AgentMode | null,
): PmRelaunchIntent {
  switch (agentMode) {
    case "building":
      return PmRelaunchIntent.Build;
    case "review":
      return PmRelaunchIntent.Review;
    default:
      break;
  }

  if (!isAuto) return PmRelaunchIntent.WaitForTeam;
  return context.plan?.trim() ? PmRelaunchIntent.AutoWithPlan : PmRelaunchIntent.AutoPlanning;
}

function buildRelaunchMessageSummary(context: TaskContext, lastAgentIdx: number): string[] {
  const newMessages = context.chatHistory.slice(lastAgentIdx + 1).filter((m) => m.role === "user");
  if (newMessages.length === 0) {
    return [`You have been relaunched. No new messages since your last session.`];
  }
  return [
    `You have been relaunched. Here are new messages since your last session:`,
    ...newMessages.map((m) => `[${m.userName ?? "user"}]: ${m.content}`),
  ];
}

function buildPmBuildRelaunchParts(context: TaskContext, isAuto?: boolean): string[] {
  const parts = [
    `\nYour plan has been approved. Begin implementing it now.`,
    `Work on the git branch "${context.githubBranch}". Stay on this branch — do not checkout or create other branches.`,
    `Start by reading the relevant source files mentioned in the plan, then write code.`,
    `When finished, use the mcp__conveyor__create_pull_request tool to open a PR. Do NOT use gh CLI.`,
  ];
  if (isAuto) {
    parts.push(
      `\nCRITICAL: You are in Auto mode. Do NOT report status, ask for confirmation, or go idle without making code changes.`,
      `Your FIRST action must be reading source files from the plan, then immediately writing code.`,
      `Do NOT summarize the plan or say "ready to implement" — start implementing.`,
      `If you are genuinely blocked, explain the specific blocker — do not go idle silently.`,
    );
  }
  return parts;
}

function buildPmReviewRelaunchParts(): string[] {
  return [
    `\nResume reviewing and coordinating this task.`,
    `Call list_subtasks to check current child-task state and progress.`,
    `Review children in ReviewPR status first, then approve and merge passing PRs.`,
    `Fire next child builds with start_child_cloud_build when ready.`,
    `Do not implement code directly or create a new PR from the PM review session.`,
  ];
}

function buildPmAutoWithPlanRelaunchParts(): string[] {
  return [
    `\nYou are in auto mode. A plan already exists for this task.`,
    `Verify that story points, title, and tags are set via get_current_plan, then call ExitPlanMode immediately to transition to building.`,
    `Do NOT wait for team input — proceed autonomously.`,
  ];
}

function buildPmAutoPlanningRelaunchParts(): string[] {
  return [
    `\nYou are in auto mode. Continue planning autonomously.`,
    `When the plan and all required properties are set, call ExitPlanMode to transition to building.`,
    `Do NOT wait for team input — proceed autonomously.`,
  ];
}

function buildPmWaitForTeamRelaunchParts(): string[] {
  return [
    `\nYou are the project manager for this task.`,
    `Review the context above and wait for the team to provide instructions before taking action.`,
  ];
}

export function buildPmRelaunchParts(
  context: TaskContext,
  lastAgentIdx: number,
  isAuto?: boolean,
  agentMode?: AgentMode | null,
): string[] {
  const parts = buildRelaunchMessageSummary(context, lastAgentIdx);
  const intent = resolvePmRelaunchIntent(context, isAuto, agentMode);
  const intentPartsByIntent: Record<PmRelaunchIntent, () => string[]> = {
    [PmRelaunchIntent.Build]: () => buildPmBuildRelaunchParts(context, isAuto),
    [PmRelaunchIntent.Review]: buildPmReviewRelaunchParts,
    [PmRelaunchIntent.AutoWithPlan]: buildPmAutoWithPlanRelaunchParts,
    [PmRelaunchIntent.AutoPlanning]: buildPmAutoPlanningRelaunchParts,
    [PmRelaunchIntent.WaitForTeam]: buildPmWaitForTeamRelaunchParts,
  };
  parts.push(...intentPartsByIntent[intent]());
  return parts;
}
